import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import Colors from "../assets/constants/Colors";
import { Spacers } from "../assets/constants/Spacers";
import { flat } from "../utils/Flat";
import { uniq } from "../utils/Uniq";
import { MOCK_POSTS } from "./Blog.constants";

const { pageContent } = Spacers;

export default function BlogArchivePage() {
  const [sp, setSp] = useSearchParams();

  const q = sp.get("q") || "";
  const category = sp.get("category") || "";
  const tag = sp.get("tag") || "";
  const year = sp.get("year") || "";
  const page = Math.max(1, parseInt(sp.get("page") || "1", 10));

  const posts = MOCK_POSTS; // replace with real data fetch
  const categories = useMemo(() => uniq(flat(posts.map(p => p.categories?.map(c => c.name) || ""))), [posts]);
  const tags = useMemo(() => uniq(flat(posts.map(p => p.tags?.map(t => t.name || "") || []))), [posts]);
  const years = useMemo(() => uniq(posts.map(p => (p.dateISO || "").slice(0,4)).filter(Boolean)).sort((a,b)=>b.localeCompare(a)), [posts]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return posts.filter(p => {
      if (category && !(p.categories || []).some(c => c.name === category)) return false;
      if (tag && !(p.tags || []).some(t => (t.name || t) === tag)) return false;
      if (year && !(p.dateISO || "").startsWith(year)) return false;
      if (needle) {
        const hay = [p.title, p.excerpt, ...(p.content || [])].join(" \\n ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [posts, q, category, tag, year]);

  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * pageSize;
  const view = filtered.slice(start, start + pageSize);

  //TODO: double check this
  interface SearchParamsPatch {
    [key: string]: string | number | null | undefined;
  }

  const updateSp = (patch: SearchParamsPatch): void => {
    const next = new URLSearchParams(sp as URLSearchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    // reset to page 1 if filters change
    if ("q" in patch || "category" in patch || "tag" in patch || "year" in patch) {
      next.set("page", "1");
    }
    setSp(next, { replace: false });
  };

  const setPage: (n: number) => void = (n: number) => updateSp({ page: n });
  const getDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-US', options);
    return formattedDate;
  }

  return (
    <PageContainer>
      <PageContentContainer>
        <FilterBar>
          <SearchInput
            type="search"
            placeholder="Search posts…"
            value={q}
            onChange={(e) => updateSp({ q: e.target.value })}
          />
          <DropdownSelect value={category} onChange={(e)=>updateSp({ category: e.target.value })}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </DropdownSelect>
          <DropdownSelect value={tag} onChange={(e)=>updateSp({ tag: e.target.value })}>
            <option value="">All Tags</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </DropdownSelect>
          <DropdownSelect value={year} onChange={(e)=>updateSp({ year: e.target.value })}>
            <option value="">All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </DropdownSelect>
          <ClearBtn onClick={() => setSp({})}>Clear</ClearBtn>
        </FilterBar>

        {view.map(p => (
          <Item key={p.id}>
            <Title>
              <Link to={`/blog/${p.slug}`}>{p.title}</Link>
            </Title>
            
            <MetaRow>
              <MetaLabel>
                {"Posted: "}
                <MetaValue>
                  {getDate(p.dateText) +", "}<Link to={`/blog?year=${(p.dateISO||"").slice(0,4)}`}>{(p.dateISO||"").slice(0,4)}</Link>
                </MetaValue>
              </MetaLabel>
              
              {p.categories?.length ? (
                <MetaLabel>
                  {"in: "} 
                  {p.categories.map((c,i)=>(
                    <MetaValue key={c.name}>
                      <Link to={`/blog?category=${encodeURIComponent(c.name)}`}>{c.name}</Link>
                      {i < p.categories.length-1 ? ", " : null}
                    </MetaValue>
                  ))}
                </MetaLabel>
              ) : null}

              {p.author?.name && (
                <MetaLabel>
                  {"by: "}
                  <MetaValue>
                    <a href={p.author.href || "andrearaylopez.com/about"}>{p.author.name}</a>
                  </MetaValue>
                </MetaLabel>)}
              
              {p.tags?.length ? (
                <MetaLabel>
                  {"Tags: "}
                  {p.tags.map((t,i)=>{
                    const name = t.name;
                    return (
                      <MetaValue key={name}>
                        <Link to={`/blog?tag=${encodeURIComponent(name)}`}>{name}</Link>
                        {i < p.tags.length-1 ? ", " : null}
                      </MetaValue>);
                  })}
                </MetaLabel>
              ) : null}

            </MetaRow>
            {p.excerpt && <Excerpt>{p.excerpt}</Excerpt>}
          </Item>
        ))}

        <PaginationNav>
          <Inner>
            <PageBtn disabled={clampedPage===1} onClick={()=> setPage(clampedPage - 1)}>‹ Prev</PageBtn>
              <Pages>
                {Array.from({length: totalPages}, (_,i) => i+1).map
                  ( n =>
                    (<li key={n}>
                        <PageBtn
                          $active={n === clampedPage} 
                          onClick={()=>setPage(n)}
                        >{n}</PageBtn>
                    </li>)
                  )
                }
              </Pages>
            <PageBtn disabled={clampedPage===totalPages} onClick={()=> setPage(clampedPage + 1)}>Next ›</PageBtn>
          </Inner>
        </PaginationNav>
      </PageContentContainer>
    </PageContainer>
  );
}

/* styled – keep minimal here; reuse your styled-components from earlier */

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const PageContentContainer = styled.div`
  max-width: ${pageContent.maxWidth.px};
  margin: 0 auto;
  padding: ${pageContent.padding.top.px} ${pageContent.padding.horizontal.px} ${pageContent.padding.bottom.px};
  overflow: auto;
`;

// Filter Bar
const FilterBar = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px 180px 120px auto;
  gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr 1fr;
  }
`;
const SearchInput = styled.input`
  border: 1px solid #ddd; border-radius: 8px; padding: 10px 12px; font-size: 14px;
`;
const DropdownSelect = styled.select`
  border: 1px solid #ddd; border-radius: 8px; padding: 10px 12px; font-size: 14px; background:#fff;
`;
const ClearBtn = styled.button`
  border: 1px solid #ddd; background:#fafafa; border-radius: 8px; padding: 10px 14px; cursor:pointer; font-size:14px;
  &:hover { background:#f2f2f2; }
`;

//Page meta info
const Item = styled.article`padding:28px 0;border-bottom:1px solid #eee;`;
const Title = styled.h2`margin:0 0 6px;font-weight:800;font-size:clamp(20px,3vw,26px);line-height:1.25;
  /* background: none;
  border: none;
  color: #111;
  text-decoration: underline; */
  text-underline-offset: 3px;
  /* text-decoration-thickness: 1px;
  font: inherit;
  cursor: pointer;
  padding: 0;
  margin: 0;
  &:hover {
    text-decoration-thickness: 2px;
  } */
`;
const MetaRow = styled.article`
  display:flex;
  flex-wrap:wrap;
  gap:6px 14px;
  align-items:baseline;
  font-size:13px;
  color:#888;
  margin-top:6px;
  a {
    text-decoration: none;
  }
`;
const MetaLabel = styled.span``;
const MetaValue = styled.span``;
const Excerpt = styled.p`margin:10px 0 0;`;

// Pagination
const PaginationNav = styled.nav`margin-top:28px;display:flex;justify-content:center;`;
const Inner = styled.div`display:flex;gap:10px;align-items:center;`;
const Pages = styled.ul`display:flex;list-style:none;padding:0;margin:0 4px;gap:4px;`;
interface PageBtnProps {
  $active?: boolean;
}
const PageBtn = styled.button<PageBtnProps>`
  border:1px solid ${({$active})=>$active?'#ddd':'#ddd'};
  background:${({$active})=>$active?'#fff':'#fff'};
  color:${Colors.globalLink};
  font-weight:${({$active})=>$active?900:400};
  border-radius:6px;
  padding:6px 10px;
  font-size:14px;
  cursor:pointer;
  &:hover{
    color:${Colors.globalHoverLink}
  }
  &:disabled{
    color:${Colors.globalLink};
    opacity:.45;
    cursor:not-allowed;
    &:hover{background:transparent}
  }
`;